import type { Dictionary } from './dictionary.js';
import { extractRuns, type Grid } from './grid.js';
import { keyOf, type CoordKey, type Letter, type Run } from './types.js';

/**
 * A blank can stay a blank.
 *
 * Making a player declare what a blank stands for is book-keeping the game
 * does not need: if there is any letter that makes every word through that
 * square real, the square is fine, and if there is none, it is not — and
 * either way the answer follows from the grid rather than from a dialog. So
 * the engine works it out, and a player only ever has to *choose* when they
 * want to force a particular letter for a reason of their own.
 *
 * This is a constraint problem: each unassigned blank is a variable over the
 * twenty-six letters, each maximal run through it is a constraint, and the
 * grid is legal if any assignment satisfies all of them. It is solved by
 * backtracking with the most-constrained blank first, testing each run the
 * moment its last blank is filled. In practice a blank sits in one or two
 * runs and there are two blanks in a tile set, so this is a few dozen
 * dictionary probes; the budget below is for the pathological grid, not the
 * ordinary one.
 *
 * Letters are tried in alphabetical order, which matters more than it looks:
 * the client and the server both resolve independently, and a deterministic
 * order is what makes them agree about which letter a blank became.
 */

const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('') as Letter[];

/** Dictionary probes before giving up. Generous; never reached in real play. */
const PROBE_BUDGET = 120_000;

export interface BlankResolution {
  /**
   * The grid with every blank standing for something, or null when no
   * assignment works at all.
   */
  readonly grid: Grid | null;
  /** What each blank was taken to mean. Empty when there were none to solve. */
  readonly inferred: ReadonlyMap<CoordKey, Letter>;
  /** Blanks the player pinned down themselves are left alone and listed here. */
  readonly chosen: ReadonlySet<CoordKey>;
  /** Why it could not be done. */
  readonly failed?: 'noLetterFits' | 'tooComplex';
}

const EMPTY: BlankResolution = {
  grid: null, inferred: new Map(), chosen: new Set(), failed: 'noLetterFits',
};

/**
 * Work out what the blanks have to be.
 *
 * Returns the original grid untouched when there is nothing to solve, so the
 * overwhelmingly common case costs one scan and no allocation.
 */
export function resolveBlanks(
  grid: Grid,
  dict: Dictionary,
  minWordLength: number,
): BlankResolution {
  const open: CoordKey[] = [];
  const chosen = new Set<CoordKey>();
  for (const [k, tile] of grid) {
    if (!tile.isBlank) continue;
    if (tile.assigned === null) open.push(k);
    else chosen.add(k);
  }
  if (open.length === 0) return { grid, inferred: new Map(), chosen };

  const runs = extractRuns(grid);

  /**
   * Which runs each blank sits in, and which blanks each run contains.
   *
   * A run too short to be a word can never be satisfied, so it is not a
   * constraint to solve — it is a grid that is already wrong, and saying so
   * here avoids searching twenty-six letters to discover it.
   */
  const runBlanks: CoordKey[][] = [];
  const runsOf = new Map<CoordKey, number[]>();
  const usable: Run[] = [];
  for (const run of runs) {
    if (run.cells.length < minWordLength) return { ...EMPTY, chosen };
    const mine = run.cells.map(keyOf).filter((k) => open.includes(k));
    const index = usable.length;
    usable.push(run);
    runBlanks.push(mine);
    for (const k of mine) {
      const list = runsOf.get(k) ?? [];
      list.push(index);
      runsOf.set(k, list);
    }
  }

  // Most constrained first: a blank in two words prunes far harder than one
  // in a single word, and trying it first is the difference between a few
  // dozen probes and a few thousand.
  const order = [...open].sort((a, b) =>
    (runsOf.get(b)?.length ?? 0) - (runsOf.get(a)?.length ?? 0)
    || a.localeCompare(b));

  const value = new Map<CoordKey, Letter>();
  let probes = 0;
  let exhausted = false;

  /** The run's letters, with blanks filled in from the current assignment. */
  const wordOf = (index: number): string | null => {
    const run = usable[index]!;
    let out = '';
    for (const cell of run.cells) {
      const k = keyOf(cell);
      const tile = grid.get(k);
      if (!tile) return null;
      if (tile.isBlank) {
        const letter = tile.assigned ?? value.get(k);
        if (!letter) return null;                  // not yet decided
        out += letter;
      } else {
        out += tile.letter ?? '';
      }
    }
    return out;
  };

  /** True when every run this blank completed is a word. */
  const satisfied = (k: CoordKey): boolean => {
    for (const index of runsOf.get(k) ?? []) {
      // Only judge a run once all of its blanks are decided.
      if (!runBlanks[index]!.every((other) => value.has(other))) continue;
      const word = wordOf(index);
      if (word === null) continue;
      probes++;
      if (probes > PROBE_BUDGET) { exhausted = true; return false; }
      if (!dict.has(word)) return false;
    }
    return true;
  };

  const search = (depth: number): boolean => {
    if (exhausted) return false;
    const k = order[depth];
    if (k === undefined) return true;
    for (const letter of ALPHABET) {
      value.set(k, letter);
      if (satisfied(k) && search(depth + 1)) return true;
      if (exhausted) { value.delete(k); return false; }
      value.delete(k);
    }
    return false;
  };

  if (!search(0)) {
    return { ...EMPTY, chosen, failed: exhausted ? 'tooComplex' : 'noLetterFits' };
  }

  // A copy, so a caller that only wanted to know never disturbs the real grid.
  const solved: Grid = new Map();
  for (const [k, tile] of grid) {
    const letter = value.get(k);
    solved.set(k, letter ? { ...tile, assigned: letter } : { ...tile });
  }
  return { grid: solved, inferred: new Map(value), chosen };
}

/**
 * Every letter that would work in one blank square, given the rest of the grid.
 *
 * For the dialog a player opens when they *want* to choose: offering all
 * twenty-six and letting them pick one that makes the word nonsense is how
 * the old prompt behaved, and it made choosing feel like a trap.
 */
export function lettersFor(
  grid: Grid,
  cell: CoordKey,
  dict: Dictionary,
  minWordLength: number,
): Letter[] {
  const tile = grid.get(cell);
  if (!tile?.isBlank) return [];
  const out: Letter[] = [];
  for (const letter of ALPHABET) {
    const trial: Grid = new Map(grid);
    trial.set(cell, { ...tile, assigned: letter });
    // Two questions, and both have to be yes. Whether the words through this
    // square are real — which `resolveBlanks` never asks, because with this
    // square decided there may be no blanks left for it to solve — and
    // whether the grid's *other* blanks can still be satisfied around it.
    const res = resolveBlanks(trial, dict, minWordLength);
    if (!res.grid) continue;
    const through = extractRuns(res.grid)
      .filter((run) => run.cells.some((c) => keyOf(c) === cell));
    const ok = through.every((run) =>
      run.cells.length >= minWordLength && !run.word.includes('?')
      && dict.has(run.word));
    if (ok) out.push(letter);
  }
  return out;
}
